import React, { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';
import toast from 'react-hot-toast';

interface BranchQRModalProps {
    branch: {
        id: string;
        name: string;
        displayName: string;
        qrCode?: string | null;
        gym: {
            name: string;
            displayName: string;
        };
    };
    onClose: () => void;
}

const BranchQRModal: React.FC<BranchQRModalProps> = ({ branch, onClose }) => {
    const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const printRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const generateQR = async () => {
            if (!branch.qrCode) {
                toast.error('QR code not available for this branch');
                setLoading(false);
                return;
            }

            try {
                const url = await QRCode.toDataURL(branch.qrCode, {
                    width: 400,
                    margin: 2,
                    color: {
                        dark: '#000000',
                        light: '#FFFFFF',
                    },
                });
                setQrCodeUrl(url);
            } catch (error) {
                console.error('Error generating QR code:', error);
                toast.error('Failed to generate QR code');
            } finally {
                setLoading(false);
            }
        };

        generateQR();
    }, [branch.qrCode]);

    const handleDownload = () => {
        if (!qrCodeUrl) return;

        const link = document.createElement('a');
        link.href = qrCodeUrl;
        link.download = `${branch.gym.displayName || branch.gym.name}-${branch.displayName || branch.name}-QR.png`;
        link.click();
        toast.success('QR code downloaded');
    };

    const handlePrint = () => {
        if (!printRef.current) return;

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.error('Please allow popups to print');
            return;
        }

        printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Branch QR Code - ${branch.displayName || branch.name}</title>
          <style>
            @media print {
              @page {
                size: A4 portrait;
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0;
                -webkit-print-color-adjust: exact;
              }
            }
            body {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 0;
              background: white;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              color: #1a1a1a;
            }
            .print-container {
              text-align: center;
              width: 100%;
              max-width: 800px;
              padding: 40px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            .header-section {
              margin-bottom: 60px;
            }
            .gym-name {
              font-size: 48px;
              font-weight: 900;
              color: #1e40af;
              margin: 0 0 12px 0;
              text-transform: uppercase;
              letter-spacing: -0.02em;
              line-height: 1;
            }
            .branch-name {
              font-size: 28px;
              font-weight: 600;
              color: #64748b;
              margin: 0;
              letter-spacing: 0.05em;
              text-transform: uppercase;
            }
            .qr-wrapper {
              background: white;
              border: 2px solid #f1f5f9;
              border-radius: 40px;
              padding: 40px;
              margin: 0 auto;
              box-shadow: 0 10px 30px rgba(0,0,0,0.03);
              display: inline-block;
            }
            .qr-image {
              display: block;
              width: 440px;
              height: 440px;
              margin: 0 auto;
            }
            .footer {
              margin-top: 80px;
              width: 100%;
              max-width: 500px;
            }
            .divider {
              height: 2px;
              background: #f1f5f9;
              margin-bottom: 24px;
              width: 100%;
            }
            .footer-content {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 12px;
              color: #94a3b8;
              font-size: 14px;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.2em;
            }
            .footer img {
              height: 24px;
              width: auto;
              opacity: 0.8;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="header-section">
              <h1 class="gym-name">${branch.gym.displayName || branch.gym.name}</h1>
              <h2 class="branch-name">${branch.displayName || branch.name}</h2>
            </div>
            
            <div class="qr-wrapper">
              <img src="${qrCodeUrl}" class="qr-image" alt="QR Code" />
            </div>

            <div class="footer">
              <div class="divider"></div>
              <div class="footer-content">
                <span>Powered by</span>
                <img src="${window.location.origin}/gympe.svg" alt="Gympe" />
              </div>
            </div>
          </div>
        </body>
      </html>
    `);

        printWindow.document.close();

        // Wait for images to load before printing
        printWindow.onload = () => {
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 250);
        };
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[1000] p-2 sm:p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-[95%] sm:max-w-xl max-h-[95vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 rounded-t-2xl sm:rounded-t-3xl flex justify-between items-center z-10">
                    <div className="pr-2">
                        <h2 className="text-xl sm:text-2xl font-bold truncate max-w-[150px] sm:max-w-none">Branch QR</h2>
                        <p className="text-blue-100 text-xs sm:text-sm mt-0.5">Scan to verify access</p>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-3">
                        <button
                            onClick={handleDownload}
                            disabled={loading || !qrCodeUrl}
                            className="p-2 sm:px-4 sm:py-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Download QR Code"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            <span className="font-semibold hidden sm:inline text-sm">Download</span>
                        </button>
                        <button
                            onClick={handlePrint}
                            disabled={loading || !qrCodeUrl}
                            className="p-2 sm:px-4 sm:py-2 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Print QR Code"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            <span className="font-semibold hidden sm:inline text-sm">Print</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 hover:bg-white/30 rounded-lg sm:rounded-xl transition-all duration-200 flex items-center justify-center"
                            title="Close"
                        >
                            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Printable Content */}
                <div ref={printRef} className="print-container p-4 sm:p-8">
                    <div className="text-center">
                        <h3 className="gym-name text-2xl sm:text-3xl font-black text-blue-700 mb-1 sm:mb-2 uppercase tracking-wide">
                            {branch.gym.displayName || branch.gym.name}
                        </h3>
                        <p className="branch-name text-xl sm:text-2xl font-bold text-gray-700 mb-6 sm:mb-8">
                            {branch.displayName || branch.name}
                        </p>
                    </div>

                    {loading ? (
                        <div className="qr-container bg-gray-50 border-4 border-gray-200 rounded-2xl p-6 sm:p-8 mx-auto inline-block">
                            <div className="w-64 h-64 sm:w-96 sm:h-96 flex items-center justify-center">
                                <div className="flex flex-col items-center gap-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                    <p className="text-gray-600 text-sm sm:text-base font-medium">Generating QR...</p>
                                </div>
                            </div>
                        </div>
                    ) : qrCodeUrl ? (
                        <div className="qr-container bg-white border border-gray-100 rounded-xl sm:rounded-2xl p-4 sm:p-6 mx-auto block max-w-[280px] sm:max-w-sm shadow-sm">
                            <img
                                src={qrCodeUrl}
                                alt="Branch QR Code"
                                className="qr-image w-full max-w-[240px] sm:max-w-[340px] mx-auto object-contain aspect-square"
                            />
                        </div>
                    ) : (
                        <div className="text-center py-10 sm:py-12">
                            <p className="text-red-600 font-semibold">Failed to generate QR code</p>
                        </div>
                    )}

                    <div className="footer mt-8 sm:mt-10 pt-4 sm:pt-6 border-t-2 border-gray-100 text-gray-400">
                        <div className="footer-content flex items-center justify-center gap-2">
                            <span className="footer-text font-bold text-[9px] sm:text-[10px] uppercase tracking-widest opacity-60">Powered by</span>
                            <img src="/gympe.svg" alt="Gympe Logo" className="h-4 sm:h-5 w-auto" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BranchQRModal;
